import { Component, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { AppNotification, NotificationType, NotificationsService, PushState } from '../stokker/api';
import { NotificationService } from '../notification/notification.service';
import { PwaService } from '../pwa/pwa.service';

// ============================================================================
// Értesítések oldal (harang): kártyák, olvasottnak jelölés, törlés, push be-/kikapcsolás.
// ============================================================================

interface NotifVM {
  n: AppNotification;
  icon: string;
  color: string;
  bg: string;
  when: string;
  initial: string;
  avatarColor: string;
}

const TYPE_META: Record<NotificationType, { icon: string; color: string; bg: string }> = {
  order_message: { icon: 'order', color: '#3F5F86', bg: '#E6EDF6' },
  ticket_comment: { icon: 'comment', color: '#4C7A2E', bg: '#E4EFD2' },
  ticket_assigned: { icon: 'assign', color: '#7A4E86', bg: '#EFE4F3' },
  ticket_due: { icon: 'clock', color: '#A14D4D', bg: '#F6E7E7' },
  reminder_due: { icon: 'clock', color: '#8A6A28', bg: '#F5EBD6' },
  mail_new: { icon: 'mail', color: '#3F5F86', bg: '#E6EDF6' },
  mail_reply: { icon: 'mail', color: '#4C7A2E', bg: '#E4EFD2' },
};
const AVATAR_PALETTE = ['#C77B9B', '#5E7FA8', '#6E9663', '#B9834A', '#9A6E9E', '#C9A45C'];

@Component({
  selector: 'app-ertesitesek',
  templateUrl: './ertesitesek.component.html',
  styleUrl: './ertesitesek.component.scss',
})
export class ErtesitesekComponent implements OnInit, OnDestroy {
  items: NotifVM[] = [];
  onlyUnread = true;
  loading = true;
  unread = 0;
  pushState: PushState = 'unsupported';
  pushBusy = false;
  private subs: Subscription[] = [];

  constructor(
    private notifications: NotificationsService,
    private router: Router,
    private toast: NotificationService,
    public pwa: PwaService,
  ) {}

  ngOnInit(): void {
    this.subs.push(
      this.notifications.unread$.subscribe((count) => (this.unread = count)),
      this.notifications.pushState$.subscribe((state) => (this.pushState = state)),
    );
    this.notifications.refreshPushState();
    this.load();
  }

  ngOnDestroy(): void {
    this.subs.forEach((s) => s.unsubscribe());
  }

  load(): void {
    this.loading = true;
    this.notifications.list(this.onlyUnread).subscribe({
      next: (rows) => {
        this.items = rows.map((n) => this.toVm(n));
        this.loading = false;
        this.notifications.refreshUnread();
      },
      error: () => {
        this.loading = false;
        this.toast.show('Az értesítések nem tölthetők be.', 'danger');
      },
    });
  }

  setFilter(onlyUnread: boolean): void {
    if (this.onlyUnread !== onlyUnread) {
      this.onlyUnread = onlyUnread;
      this.load();
    }
  }

  /** Kattintás: olvasott + ugrás oda, ahol a dolog történt. */
  open(vm: NotifVM): void {
    if (!vm.n.readAt) {
      vm.n.readAt = new Date().toISOString();
      this.notifications.markRead(vm.n.id).subscribe({ error: () => undefined });
    }
    this.router.navigateByUrl(vm.n.link);
  }

  toggleRead(vm: NotifVM, event: Event): void {
    event.stopPropagation();
    const read = !vm.n.readAt;
    this.notifications.markRead(vm.n.id, read).subscribe({
      next: (n) => {
        vm.n = n;
        if (this.onlyUnread && read) {
          this.items = this.items.filter((i) => i !== vm);
        }
      },
      error: () => this.toast.show('Nem sikerült.', 'danger'),
    });
  }

  remove(vm: NotifVM, event: Event): void {
    event.stopPropagation();
    this.items = this.items.filter((i) => i !== vm);
    this.notifications.remove(vm.n.id).subscribe({ error: () => this.toast.show('Nem sikerült törölni.', 'danger') });
  }

  markAllRead(): void {
    this.notifications.markAllRead().subscribe({
      next: () => {
        this.items.forEach((vm) => (vm.n.readAt = vm.n.readAt || new Date().toISOString()));
        if (this.onlyUnread) {
          this.items = [];
        }
      },
      error: () => this.toast.show('Nem sikerült.', 'danger'),
    });
  }

  clearRead(): void {
    this.notifications.removeAllRead().subscribe({
      next: () => this.load(),
      error: () => this.toast.show('Nem sikerült.', 'danger'),
    });
  }

  // ---------------------------------------------------------------- push

  async enablePush(): Promise<void> {
    this.pushBusy = true;
    const error = await this.notifications.enablePush();
    this.pushBusy = false;
    if (error) {
      this.toast.show(error, 'warning', 6000);
    } else {
      this.toast.show('Értesítések bekapcsolva ezen az eszközön.', 'success');
    }
  }

  async disablePush(): Promise<void> {
    this.pushBusy = true;
    await this.notifications.disablePush();
    this.pushBusy = false;
    this.toast.show('Értesítések kikapcsolva ezen az eszközön.', 'success');
  }

  sendTest(): void {
    this.notifications.sendTest().subscribe({
      next: () => this.toast.show('Próba-értesítés elküldve — pár másodpercen belül meg kell jelennie.', 'success'),
      error: () => this.toast.show('Nem sikerült.', 'danger'),
    });
  }

  /** iPhone-on csak a kezdőképernyőre telepített appban van push. */
  get iosNeedsInstall(): boolean {
    const ua = navigator.userAgent;
    const ios = /iPad|iPhone|iPod/.test(ua) || (/Macintosh/.test(ua) && navigator.maxTouchPoints > 1);
    return ios && !this.pwa.isStandalone;
  }

  trackVm(_: number, vm: NotifVM): number {
    return vm.n.id;
  }

  private toVm(n: AppNotification): NotifVM {
    const meta = TYPE_META[n.type] || TYPE_META.ticket_comment;
    const name = n.actor?.name || '';
    let hash = 0;
    for (const ch of name) hash = (hash * 31 + ch.charCodeAt(0)) >>> 0;
    return {
      n,
      icon: meta.icon,
      color: meta.color,
      bg: meta.bg,
      when: this.whenLabel(n.createdAt),
      initial: (name.trim().charAt(0) || '').toUpperCase(),
      avatarColor: AVATAR_PALETTE[hash % AVATAR_PALETTE.length],
    };
  }

  private whenLabel(iso: string): string {
    const date = new Date(iso);
    const diffMin = Math.round((Date.now() - date.getTime()) / 60000);
    if (diffMin < 1) return 'most';
    if (diffMin < 60) return `${diffMin} perce`;
    if (diffMin < 24 * 60 && date.toDateString() === new Date().toDateString()) return `ma ${date.toLocaleTimeString('hu-HU', { hour: '2-digit', minute: '2-digit' })}`;
    const yesterday = new Date(); yesterday.setDate(yesterday.getDate() - 1);
    if (date.toDateString() === yesterday.toDateString()) return `tegnap ${date.toLocaleTimeString('hu-HU', { hour: '2-digit', minute: '2-digit' })}`;
    return date.toLocaleString('hu-HU', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
  }
}
